import { memo, useCallback } from 'react';
import { Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useIntegrations } from '../context';
import { useIntegrationsPermissions } from '../hooks';
import { ArrowRight, MessageSquare, PenLine } from 'lucide-react';

/**
 * One promo banner (prototype `.build-promo`): a tinted full-width surface with
 * an icon in a rounded box, a title + description, and a right-aligned CTA.
 * Pure/presentational — the parent owns the copy, styling tokens and action.
 */
function PromoBanner({ icon: Icon, title, body, className, iconClassName, action }) {
  return (
    <div
      className={`flex flex-col items-start gap-4 rounded-xl border p-5 sm:flex-row sm:items-center sm:gap-5 ${className}`}
    >
      <span
        aria-hidden="true"
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${iconClassName}`}
      >
        <Icon size={22} />
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-bold text-content">{title}</h3>
        <p className="mt-0.5 text-xs leading-relaxed text-content-muted">{body}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

/**
 * Marketplace promo banners (CT-4165): "Build your own" (admin-only) and "Request
 * it from the AIMS team" (all roles → CT-4175 modal), matching the prototype's two
 * stacked `.build-promo` banners.
 *
 * CT-3894 is deferred and Data Studio is not built, so — overriding the prototype,
 * which links Build to `#/build` — the Build-your-own CTA renders disabled with a
 * "Coming soon" affordance and never navigates. `canManageIntegrations` is a V1
 * stub (true for all) until the real RBAC gate lands (CT-3900).
 */
function MarketplacePromoBanners() {
  const { t } = useTranslation('admin-studio');
  const { openRequestIntegration } = useIntegrations();
  const { canManageIntegrations } = useIntegrationsPermissions();

  // Stable, arg-less handler — avoids a new inline arrow per render and never
  // leaks the click event into `openRequestIntegration`'s payload (CT-4175).
  const handleRequest = useCallback(() => openRequestIntegration(), [openRequestIntegration]);

  return (
    <div className="flex flex-col gap-3">
      {canManageIntegrations && (
        <PromoBanner
          icon={PenLine}
          title={t('integrations.marketplace.promo.build.title')}
          body={t('integrations.marketplace.promo.build.body')}
          className="border-dashed border-violet-500/30 bg-gradient-to-br from-violet-500/[0.06] to-cyan-500/[0.06]"
          iconClassName="bg-violet-500/10 text-violet-600 dark:text-violet-400"
          action={
            // TODO(CT-3894): wire to Data Studio once it exists. Deferred → disabled,
            // "Coming soon", never navigates.
            <Button
              variant="tertiary"
              size="sm"
              disabled
              title={t('integrations.marketplace.promo.comingSoon')}
            >
              {t('integrations.marketplace.promo.build.cta')}
              <span className="ml-1.5 text-xs text-content-muted">
                · {t('integrations.marketplace.promo.comingSoon')}
              </span>
            </Button>
          }
        />
      )}

      <PromoBanner
        icon={MessageSquare}
        title={t('integrations.marketplace.promo.request.title')}
        body={t('integrations.marketplace.promo.request.body')}
        className="border-amber-500/30 bg-gradient-to-br from-amber-500/[0.05] to-cyan-500/[0.05]"
        iconClassName="bg-amber-500/10 text-amber-600 dark:text-amber-400"
        action={
          <Button
            variant="secondary"
            size="sm"
            rightIcon={ArrowRight}
            className="dark:!text-white"
            onClick={handleRequest}
          >
            {t('integrations.marketplace.promo.request.cta')}
          </Button>
        }
      />
    </div>
  );
}

export default memo(MarketplacePromoBanners);
